import Application from "../../Application";
import withStore, {
  TLoadingAwarePresenter,
  TPresentable
} from "../../core/presenter/withStore";
import { IOrganisationIndex } from "../../organisations/model/OrganisationIndex";
import { ILegalView } from "../model/LegalView";
import { IOrganisation } from "../../organisations/model/Organisation";

import LegalViewPresentationModel, {
  TLegalViewPresentationModel
} from "./model/LegalViewPresentationModel";
import { IUser } from "../../user/model/User";
import { TAttachmentTypePresentationModel } from "../../organisations/presenter/model/AttachmentTypePresentationModel";
import { IGenericCodebookItem } from "../../codebooks/model/GenericCodebookItem";
import { TAttachmentPresentationModel } from "../../caseLawDocuments/presenter/model/AttachmentPresentationModel";
import { IAttachmentType } from "../../attachmentType/model/AttachmentType";
import { IAttachment } from "../../caseLawDocuments/model/Attachment";
import OrganisationIndexPresentationModel, {
  TOrganisationIndexPresentationModel
} from "../../organisations/presenter/model/OrganisationIndexPresentationModel";
import {
  IDocumentType,
  TDocumentType
} from "../../documentTypes/model/DocumentType";
import PublishLegalViewInteractor from "../interactor/PublishLegalViewInteractor";
import ArchiveLegalViewInteractor from "../interactor/ArchiveLegalViewInteractor";
import FindLegalViewInteractor from "../interactor/FindLegalViewInteractor";
import RevertLegalViewStatusInteractor from "../interactor/RevertLegalViewStatusInteractor";
import DeleteLegalViewInteractor from "../interactor/DeleteLegalViewInteractor";
import ShowSuccessMessageInteractor from "../../core/interactor/notifications/ShowSuccessMessageInteractor";

export interface TLegalViewDetailsPresenter extends TLoadingAwarePresenter {
  legalView: TLegalViewPresentationModel;
  organisations: IOrganisation[];
  legalViewTypes: TDocumentType[];
  legalViewIndexes: TOrganisationIndexPresentationModel[];
  indexHierarchyList: string[][];
  selectedLanguage?: any;
  user?: IUser;
  attachmentTypes: TAttachmentTypePresentationModel[];
  attachmentContentTypes: IGenericCodebookItem[];
  attachments: TAttachmentPresentationModel[];
  anonimisedUploadedFile: any;
}

export interface ILegalViewDetailsPresenter
  extends TLegalViewDetailsPresenter,
    TPresentable {
  loadLegalView(legalViews: ILegalView): void;
  loadReferenceValues(
    legalViewTypes: IDocumentType[],
    organisations: IOrganisation[],
    attachmentTypes: IAttachmentType[],
    attachments: IAttachment[],
    attachmentContentTypes: IGenericCodebookItem[]
  ): void;
  loadLegalViewIndexes(legalViewIndexes: IOrganisationIndex[]);
  showLegalViewUpdatePage(id: number): void;
  canEdit(organisationId: number): boolean;
  showLegalViewDetailsPage(id: number): void;
  publishLegalView(): void;
  archiveLegalView(): void;
  setAnonymisedFileValue(file: any): void;
  revertLegalViewStatus(): void;
  deleteLegalView(): void;
}

const defaultState: TLegalViewDetailsPresenter = {
  legalView: {} as TLegalViewPresentationModel,
  organisations: [],
  legalViewTypes: [],
  legalViewIndexes: [],
  indexHierarchyList: [],
  attachmentTypes: [],
  attachmentContentTypes: [],
  attachments: [],
  anonimisedUploadedFile: undefined
};

const LegalViewDetailsPresenter = withStore<
  ILegalViewDetailsPresenter,
  TLegalViewDetailsPresenter
>(({ application, store, loader, translate }): ILegalViewDetailsPresenter => {
  const _store = store;
  const _application: Application = application;
  const state = _store.getState<TLegalViewDetailsPresenter>();
  loader.start("legalViewDetailsLoader");
  const loadLegalView = (item: ILegalView) => {
    const { organisations, legalViewTypes } =
      _store.getState<TLegalViewDetailsPresenter>();

    const legalViewType = legalViewTypes?.find(
      (value) => value.id === item.documentTypeId
    );
    const organisation = organisations?.find(
      (value) => value.id === item.organisationId
    );
    const { selectedLanguage } = _store.getState<any>();
    const summary = item.summaryTranslations.find(
      (element) => element.languageId === selectedLanguage.id
    )?.summaryTranslation;
    const legalView = LegalViewPresentationModel(
      item,
      legalViewType,
      organisation,
      summary
    );
    _store.update({
      legalView,
      breadCrumb: {
        id: () => legalView?.name
      }
    });
    loader.stop("legalViewDetailsLoader");
  };
  const loadReferenceValues = (
    legalViewTypes: IDocumentType[],
    organisations: IOrganisation[],
    attachmentTypes: IAttachmentType[],
    attachments: IAttachment[],
    attachmentContentTypes: IGenericCodebookItem[]
  ) => {
    const user = _store.getState<any>().user;
    const selectedLanguage = _store.getState<any>().selectedLanguage;
    _store.update({
      legalViewTypes,
      organisations,
      attachmentTypes,
      attachments,
      attachmentContentTypes,
      selectedLanguage,
      user
    });
  };

  const loadLegalViewIndexes = (legalViewIndexes: IOrganisationIndex[]) => {
    const { selectedLanguage } = _store.getState<any>();
    _store.update({
      legalViewIndexes: legalViewIndexes.map((item) =>
        OrganisationIndexPresentationModel(item, selectedLanguage)
      )
    });

    prepareHierarchyList();
  };

  const canEdit = (organisationId) => {
    const { user } = _store.getState<TLegalViewDetailsPresenter>();
    if (user) {
      return user.canEdit(organisationId);
    } else {
      return false;
    }
  };

  const getCorrectIndexTranslation = (index: any) => {
    const { selectedLanguage } = store.getState<any>();
    const nameTranslation = index.nameTranslations.find(
      (item) => item.languageId === selectedLanguage?.id
    )?.nameTranslation;
    return nameTranslation ?? index.name;
  };

  const getCorrectGroupTranslation = (data?: any) => {
    const { selectedLanguage } = store.getState<any>();
    if (data) {
      const nameTranslation = data.nameTranslations.find(
        (item) => item.languageId === selectedLanguage?.id
      )?.nameTranslation;
      return nameTranslation ?? data.name;
    } else {
      return "";
    }
  };
  const findParent = (group, groupList, indexHierarchyList) => {
    if (group?.parentId) {
      let g = groupList.find((groupItem) => groupItem.id === group?.parentId);
      if (g) {
        indexHierarchyList.push(getCorrectGroupTranslation(g));
        return findParent(g, groupList, indexHierarchyList);
      }
    } else if (group?.organisationIndexCategoryId) {
      indexHierarchyList.push(group?.categoryName);
    }
    return indexHierarchyList;
  };

  const prepareHierarchyList = () => {
    let indexHierarchyList = state.indexHierarchyList;
    const { legalViewIndexes } = _store.getState<TLegalViewDetailsPresenter>();
    legalViewIndexes.forEach((index) => {
      let indexHierarchy: string[] = [];
      let firstGroup = index.groups.find(
        (g) => g.id === index.organisationGroupId
      );
      indexHierarchy.push(getCorrectIndexTranslation(index));
      indexHierarchy.push(getCorrectGroupTranslation(firstGroup));
      indexHierarchy = findParent(firstGroup, index.groups, indexHierarchy);
      indexHierarchyList.push(indexHierarchy);
    });

    _store.update({ indexHierarchyList });
  };
  loader.start("legalViewDetailsLoader");

  const showLegalViewUpdatePage = (id: number) => {
    return _application.navigator.push({
      pathname: `/legal-views/${id.toString()}/update`
    });
  };

  const setAnonymisedFileValue = (file: any) => {
    _store.update({ anonimisedUploadedFile: file });
  };

  const showLegalViewDetailsPage = (id: number) => {
    return _application.navigator.push({
      pathname: `/legal-views/${id.toString()}/details`
    });
  };

  const publishLegalView = async () => {
    loader.start("legalViewDetailsLoader");
    const { legalView, legalViewTypes, organisations } =
      _store.getState<TLegalViewDetailsPresenter>();

    await _application.container
      .resolve<PublishLegalViewInteractor>("publishLegalView")
      .execute(legalView.id);
    let item = await _application.container
      .resolve<FindLegalViewInteractor>("findLegalView")
      .execute(legalView.id);
    const legalViewType = legalViewTypes?.find(
      (value) => value.id === item.documentTypeId
    );
    const organisation = organisations?.find(
      (value) => value.id === item.organisationId
    );
    let model = LegalViewPresentationModel(item, legalViewType, organisation);
    _store.update({ legalView: model });
    loader.stop("legalViewDetailsLoader");
  };

  const archiveLegalView = async () => {
    loader.start("legalViewDetailsLoader");
    const { legalView, legalViewTypes, organisations } =
      _store.getState<TLegalViewDetailsPresenter>();

    await _application.container
      .resolve<ArchiveLegalViewInteractor>("archiveLegalView")
      .execute(legalView.id);
    let item = await _application.container
      .resolve<FindLegalViewInteractor>("findLegalView")
      .execute(legalView.id);
    const legalViewType = legalViewTypes?.find(
      (value) => value.id === item.documentTypeId
    );
    const organisation = organisations?.find(
      (value) => value.id === item.organisationId
    );
    let model = LegalViewPresentationModel(item, legalViewType, organisation);
    _store.update({ legalView: model });
    loader.stop("legalViewDetailsLoader");
  };

  const revertLegalViewStatus = async () => {
    const { legalView, legalViewTypes, organisations } =
      _store.getState<TLegalViewDetailsPresenter>();
    loader.start("legalViewDetailsLoader");
    await _application.container
      .resolve<RevertLegalViewStatusInteractor>("revertLegalViewStatus")
      .execute(legalView.id);
    let item = await _application.container
      .resolve<FindLegalViewInteractor>("findLegalView")
      .execute(legalView.id);
    const legalViewType = legalViewTypes?.find(
      (value) => value.id === item.documentTypeId
    );
    const organisation = organisations?.find(
      (value) => value.id === item.organisationId
    );
    let model = LegalViewPresentationModel(item, legalViewType, organisation);
    _store.update({ legalView: model });
    loader.stop("legalViewDetailsLoader");
  };

  const showLegalViewList = () => {
    return _application.navigator.replace({
      pathname: `/search`
    });
  };

  const deleteLegalView = async () => {
    const { legalView } = _store.getState<TLegalViewDetailsPresenter>();

    await _application.container
      .resolve<DeleteLegalViewInteractor>("deleteLegalView")
      .execute(legalView.id);
    _application.container
      .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
      .execute(translate("editors.project.create_success_message"));
    showLegalViewList();
  };

  return {
    ...state,
    store: _store,
    loader,
    application: _application,
    translate,
    loadLegalView,
    loadReferenceValues,
    loadLegalViewIndexes,
    showLegalViewUpdatePage,
    showLegalViewDetailsPage,
    publishLegalView,
    canEdit,
    archiveLegalView,
    setAnonymisedFileValue,
    revertLegalViewStatus,
    deleteLegalView
  };
}, defaultState);

export default LegalViewDetailsPresenter;
